// Composer preview pane.
//
// Renders the formatted markdown body the same way the timeline does,
// so users see the exact result of their bold / italic / link / quote
// strokes. Auto-hides on empty input — the toggle stays on, the panel
// just collapses, so re-typing brings the preview back without an
// extra click.

import { MarkdownBody } from "~/lib/markdown-render";
import { RoomLinkContext } from "~/lib/permalink";

export interface FormattingPreviewProps {
  /** The composer's current value. */
  body: string;
}

export function FormattingPreview({ body }: FormattingPreviewProps) {
  // Auto-hide on empty / whitespace-only input. The toggle stays on
  // (the parent owns that flag); the panel collapses so the composer
  // doesn't grow vertically for nothing.
  if (body.trim().length === 0) return null;

  return (
    <div
      data-testid="composer-formatting-preview"
      role="region"
      aria-label="Formatted preview"
      style={{
        borderTop: "1px solid var(--chat-surface-border)",
        padding: "var(--chat-space-3)",
        maxHeight: 200,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: "var(--chat-space-2)",
      }}
    >
      <span
        style={{
          color: "var(--chat-fg-muted)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-xs-size)",
          lineHeight: "var(--chat-type-body-xs-line)",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        Preview
      </span>
      <div
        style={{
          color: "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-lg-size)",
          lineHeight: "var(--chat-type-body-lg-line)",
          fontWeight: 300,
        }}
      >
        <RoomLinkContext.Provider value={null}>
          <MarkdownBody text={body} />
        </RoomLinkContext.Provider>
      </div>
    </div>
  );
}
